import { useRollbar } from '@rollbar/react'
import {
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import { Stripe } from '@stripe/stripe-js'
import {
  BadRequest,
  InternalError,
  NetworkError,
  ServiceUnavailable,
} from 'common/errors'
import { BookingPaidCalendarInterface } from 'common/types/entities/booking-paid-calendar-interface'
import { OldEntityInterface } from 'common/types/entities/OldEntityInterface'
import { TwoStepPaymentFormStepInterface } from 'common/types/entities/two-step-payment-form-interface'
import { optInFail } from 'publisher/actions/optInActions'
import { buyMainOffer, confirmPaymentIntent } from 'publisher/api/stripe'
import { STRIPE_IGNORED_ERROR_CODES_LIST } from 'publisher/constants/payment-consts'
import { IntentTypeEnum } from 'publisher/enums/IntentTypeEnum'
import usePayment, {
  selectors as paymentSelectors,
} from 'publisher/hooks/usePayment'
import React from 'react'
import { useDispatch } from 'react-redux'
import { PaymentButtonEntityInterface } from 'src/shared/entities/payment-button/payment-button.schema'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import usePage, { selectors as pageSelectors } from '../usePage'
import usePaymentSubmit from './usePaymentSubmit'

const getStripeConfirmHandler = (stripe: Stripe, type: IntentTypeEnum) =>
  type === IntentTypeEnum.setup
    ? stripe.confirmCardSetup
    : stripe.confirmCardPayment

const useStripeCardPaymentHandler = (
  entity:
    | OldEntityInterface
    | PaymentButtonEntityInterface
    | BookingPaidCalendarInterface
    | TwoStepPaymentFormStepInterface,
) => {
  const dispatch = useDispatch()
  const { t } = useTypedTranslation()
  const rollbar = useRollbar()
  const stripe = useStripe()
  const elements = useElements()
  const purchaseProcessId = usePayment(paymentSelectors.getPurchaseProcessId)
  const pageId = usePage(pageSelectors.getPageId)
  const { errors, setErrors, isLoading, submit } = usePaymentSubmit(entity)
  const isReady = !!stripe && !!elements

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault()

    if (!stripe || !elements) {
      return
    }

    await submit(
      async body => {
        // does not actually submit anything, validates stripe elements
        // https://docs.stripe.com/js/elements/submit
        const { error: initError } = await elements.submit()

        if (initError) {
          throw new Error('Stripe elements validation failed')
        }

        const cardElement = elements.getElement(CardNumberElement)

        if (!cardElement) {
          setErrors([t('core.errors.stripe_data_error')])
          return
        }

        const { paymentMethod, error } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
        })

        if (error) {
          // @see https://systeme-team.slack.com/archives/C6UURE03D/p1670840092361989?thread_ts=1668177169.203109&cid=C6UURE03D
          setErrors([error.message as string])
          // setErrors([
          //   t('components.payment_methods.stripe.invalid_card_details'),
          // ])
          return
        }

        if (!paymentMethod) {
          throw new Error('undefined payment method')
        }

        const { data: mainOfferData } = await buyMainOffer(
          pageId,
          purchaseProcessId,
          {
            payment_form: {
              ...body,
              paymentMethodId: paymentMethod.id,
            },
          },
        )

        if (mainOfferData.intentSecret) {
          const mainOfferStripeConfirmHandler = getStripeConfirmHandler(
            stripe,
            mainOfferData.intentType,
          )

          const { error } = await mainOfferStripeConfirmHandler(
            mainOfferData.intentSecret,
            { payment_method: paymentMethod.id },
          )

          if (!error) {
            const { data: bumpData } = await confirmPaymentIntent(
              pageId,
              purchaseProcessId,
            )
            // bump case
            if (bumpData.intentSecret) {
              const bumpOfferStripeConfirmHandler = getStripeConfirmHandler(
                stripe,
                bumpData.intentType,
              )

              const { error } = await bumpOfferStripeConfirmHandler(
                bumpData.intentSecret,
                { payment_method: paymentMethod.id ?? { card: cardElement } },
              )

              if (!error) {
                const { data } = await confirmPaymentIntent(
                  pageId,
                  purchaseProcessId,
                )
                window.location.assign(data.redirect)
                return
              } else {
                if (
                  error.code &&
                  !STRIPE_IGNORED_ERROR_CODES_LIST.includes(error.code)
                ) {
                  rollbar.error('stripe bump offer error', error)
                }
                setErrors([error.message as string])
              }
            }

            // redirect to upSell or thank you page
            window.location.assign(mainOfferData.redirect)
          } else {
            if (
              error.code &&
              !STRIPE_IGNORED_ERROR_CODES_LIST.includes(error.code)
            ) {
              rollbar.error('stripe main offer error', error)
            }
            setErrors([error.message as string])
          }
        } else {
          // redirect to upSell or thank you page
          window.location.href = mainOfferData.redirect
        }
      },
      (error, body) => {
        if (error instanceof BadRequest) {
          setErrors(error.response.data.errors.common)
          dispatch(optInFail({ fields: error.response.data.errors.fields }))
        } else if (error instanceof NetworkError) {
          setErrors([t('core.errors.no_connection')])
        } else if (error instanceof InternalError) {
          setErrors([t('core.error.title')])
        } else if (error instanceof ServiceUnavailable) {
          setErrors([t('core.errors.service_temporary_unavailable')])
        } else {
          setErrors(
            Array.isArray(error)
              ? error
              : [t('core.errors.went_wrong_contact_support')],
          )

          if (error instanceof Error) {
            rollbar.captureEvent(body!, 'debug')
            rollbar.error('stripe submit error', error as Error)
          }
        }
      },
    )
  }

  return { errors, isReady, isLoading, handleSubmit }
}

export default useStripeCardPaymentHandler
